// The tool lineup on the raisedbeef.com homepage, read from the registry.
//
// Sibling of rbHomeKpis.ts and bound by the same two rules, for the same
// reasons: shared by the build-time prerender AND the client refresh, so the
// HTML Google reads cannot disagree with the HTML a visitor ends up with; and
// deliberately NOT using @/integrations/supabase/client, which touches
// `localStorage` at module scope and throws under Node during prerender.
//
// Source is `rpc_rb_tools`, anon-callable and SECURITY INVOKER (migration
// 20260907000009). Editable at raisedbeef.com/manage/tools.
//
// ── One difference from the KPI module that matters ───────────────────────
// `fetchHomeKpis` falls back to EMPTY_KPIS: an empty tile renders a dash and
// the page survives. An empty TOOL GRID is the page missing — the homepage is
// a launcher, and a launcher with nothing to launch is broken. So the fallback
// here is the real compiled-in lineup from tools.ts, never an empty array.
// It stands in for an UNREACHABLE database only — an empty lineup the database
// actually returned is passed through (see usable()).

import { isPlaceholder, RB_CATEGORIES, RB_TOOLS, type RbAccess } from "@/pages/rbnext/tools";

export interface RbToolRow {
  slug: string;
  name: string;
  category: string;
  iconName: string;
  href: string;
  access: RbAccess;
  description: string | null;
  sortOrder: number;
  /** Lifted to the top of its column for the requested day. */
  featured: boolean;
}

export interface RbCategoryRow {
  name: string;
  live: boolean;
  sortOrder: number;
}

export interface RbToolsPayload {
  categories: RbCategoryRow[];
  tools: RbToolRow[];
  /** Lets the heading say "Monday's Top Tools" only when something actually
   *  rose to the top, instead of promising a curation that did not happen. */
  featuredCount: number;
  /** Day-INDEPENDENT: does this site feature anything, on any day at all?
   *
   *  Baked by the prerender so the "today's picks" strip can reserve its space
   *  before the client knows the visitor's day. Without it the strip would drop
   *  in after hydration and shove the grid down. */
  anyFeatured: boolean;
}

/**
 * The compiled-in lineup — what the page renders when the database cannot be
 * reached, and what seeds the registry on a fresh install.
 *
 * Built from the same array the seed migration was written from, so an offline
 * visitor sees the lineup the site shipped with rather than nothing.
 */
export const RB_TOOLS_FALLBACK: RbToolsPayload = {
  categories: RB_CATEGORIES.map((c, i) => ({
    name: c.name,
    live: "live" in c && c.live === true,
    sortOrder: i,
  })),
  tools: RB_TOOLS.map((t, i) => ({
    slug: t.slug,
    name: t.name,
    category: t.category,
    iconName: t.iconName,
    href: t.href,
    access: t.access,
    description: t.shortDescription,
    sortOrder: i,
    // Nothing is featured in the fallback. Featuring is a property of a DAY,
    // and the fallback has no idea what day it is being used on.
    featured: false,
  })),
  featuredCount: 0,
  // The fallback is used when the database is unreachable, and it has no idea
  // what is scheduled — so it reserves nothing rather than reserving wrongly.
  anyFeatured: false,
};

/**
 * True when the database's answer can be drawn as-is.
 *
 * ⚠️ AN EMPTY TOOL LIST IS A REAL ANSWER, NOT AN OUTAGE (owner, 2026-09-13).
 * This used to send `tools: []` to the fallback like a failed request. With
 * nothing marked Popular and nothing scheduled, the database says "no picks
 * today", and the homepage drew the compiled-in tools.ts lineup instead — six
 * buttons under "Popular tools" that /manage/tools showed nowhere, so the
 * admin page looked broken. The fallback is for when the database cannot be
 * reached. A caller that must never draw an empty grid (RbNextHome) keeps its
 * own `tools.length` guard.
 *
 * A non-empty lineup of nothing but placeholders is still unusable.
 */
function usable(p: RbToolsPayload): boolean {
  return p.tools.length === 0 || p.tools.some((t) => !t.href.startsWith("/TODO-"));
}

/**
 * Fetch the lineup for a given day.
 *
 * ⚠️ NEVER REJECTS. Missing env, non-ok response, malformed JSON, no
 * categories, a lineup of only placeholders — every one of them returns
 * RB_TOOLS_FALLBACK. An empty tool list from a healthy response is returned
 * as-is: it means "nothing to show today". The prerender script and the
 * homepage both call this, and neither has anything useful to do with an
 * exception: the correct behaviour in all cases is "draw the lineup we shipped
 * with".
 *
 * ⚠️ Both arguments omitted means NO SCHEDULING APPLIED — the base lineup in
 * declared order. That is deliberately what the build-time prerender asks for.
 * The build machine's day has nothing to do with the visitor's day, which is
 * the whole argument in useWeekday.ts; baking a Tuesday lineup into a document
 * served all week would be worse than baking none.
 *
 * @param onDate  YYYY-MM-DD in the VISITOR's local time, not UTC.
 * @param weekday 0–6, 0 = Sunday, matching Date.getDay() and EXTRACT(DOW).
 */
export async function fetchRbTools(
  onDate?: string | null,
  weekday?: number | null,
): Promise<RbToolsPayload> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) return RB_TOOLS_FALLBACK;

  try {
    const res = await fetch(`${url}/rest/v1/rpc/rpc_rb_tools`, {
      method: "POST",
      headers: {
        apikey: key,
        Authorization: `Bearer ${key}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        p_on_date: onDate ?? null,
        p_weekday: weekday ?? null,
      }),
    });
    if (!res.ok) return RB_TOOLS_FALLBACK;

    const data = (await res.json()) as Partial<RbToolsPayload> | null;
    const payload: RbToolsPayload = {
      categories: data?.categories ?? [],
      tools: data?.tools ?? [],
      featuredCount: data?.featuredCount ?? 0,
      anyFeatured: data?.anyFeatured ?? false,
    };
    if (!payload.categories.length || !usable(payload)) return RB_TOOLS_FALLBACK;
    return payload;
  } catch {
    return RB_TOOLS_FALLBACK;
  }
}

/** Whether this payload is the compiled-in one. The prerender warns on it —
 *  a build that silently shipped last release's lineup while reporting success
 *  is exactly the failure the prerender guards exist to catch. */
export function isFallback(p: RbToolsPayload): boolean {
  return p === RB_TOOLS_FALLBACK;
}

export { isPlaceholder };
